import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, BarChart3, Users, Database, ArrowLeft } from 'lucide-react';
import BottomTabNavigation from '@/components/navigation/BottomTabNavigation';
import {
  NutritionB2BAnalytics,
  NutritionProviderAssignment,
  NutritionBrandNormalization
} from '@/components/nutrition/admin';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const NutritionAdminPage: React.FC = () => {
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/auth');
          return;
        }

        const { data: adminCheck } = await supabase.rpc('is_admin', {
          user_uuid: user.id
        });

        setIsAdmin(!!adminCheck);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 container mx-auto max-w-7xl p-4 pb-20">
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="animate-pulse text-muted-foreground">
                {t('nutritionAdmin.verifyingPermissions')}
              </div>
            </CardContent>
          </Card>
        </div>
        <BottomTabNavigation />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 container mx-auto max-w-4xl p-4 pb-20">
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <Shield className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('nutritionAdmin.accessDenied')}</h3>
                <p className="text-muted-foreground">
                  {t('nutritionAdmin.accessDeniedMessage')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        <BottomTabNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 container mx-auto max-w-7xl p-4 pb-20 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <BarChart3 className="h-7 w-7 text-primary" />
                {t('nutritionAdmin.panelTitle')}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t('nutritionAdmin.panelDescription')}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="text-green-600 border-green-300">
            <Shield className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-[500px]">
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">{t('nutritionAdmin.tabs.analytics')}</span>
            </TabsTrigger>
            <TabsTrigger value="assignment" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">{t('nutritionAdmin.tabs.assignment')}</span>
            </TabsTrigger>
            <TabsTrigger value="normalization" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              <span className="hidden sm:inline">{t('nutritionAdmin.tabs.data')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            <NutritionB2BAnalytics />
          </TabsContent>

          <TabsContent value="assignment">
            <NutritionProviderAssignment />
          </TabsContent>

          <TabsContent value="normalization">
            <NutritionBrandNormalization />
          </TabsContent>
        </Tabs>
      </div>
      <BottomTabNavigation />
    </div>
  );
};

export default NutritionAdminPage;
